import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { getCheckoutCart } from '../api/botApi';
import {
  setWholeCart,
  agentCommissionPercent,
  ShoppingCartV2,
} from '../../../modules/ShoppingCart/ShoppingCartSliceV2';
import { generateUUID } from '../../../modules/ShoppingCart/hooks/useHandleCheckout';
import type { RootState } from '../../../store/store';
import { calculateCartTotalsUtil } from '../../../utils/cartPricing';
import type { ratesInterface } from '../../../interfaces';

/**
 * The `agent_commission.percentage` a bot checkout must post.
 *
 * Every B2C add-to-cart reducer seeds it in `initialStateShoppingCart`:
 *
 *   agent?.price_view === PriceView.Gross
 *     ? (agent_commission_percent_from_api || 0)
 *     : 0
 *
 * where the "from api" value is the FIRST product's
 * `agent_additional_info.suggested_commission`. The bot hand-off runs no
 * reducer, so the BE computes the same rule from the session's price view and
 * the cart it captured (sky360-ms bot-agent `botAgentCommission`) and stamps it
 * on the checkout cart as `agent_commission_percent`. Absent (an older cached
 * cart) means 0 — the safe direction: it can only omit an agent's own uplift,
 * never invent a commission that was never quoted.
 */
export function botCheckoutAgentCommissionPercent(
  cart: Partial<ShoppingCartV2> | null | undefined,
): number {
  const pct = Number((cart as any)?.agent_commission_percent);
  return Number.isFinite(pct) && pct > 0 && pct < 100 ? pct : 0;
}

/** Mirrors ToolExecutorService.CHECKOUT_URL_TTL_MS on the BE. */
const BOT_CHECKOUT_TTL_MS = 30 * 60 * 1000;
const BOT_CHECKOUT_INDEX_KEY = 'bot_checkout_sessions';

interface BotCheckoutSessionEntry {
  uuid: string;
  expiresAt: number;
}

function readCheckoutIndex(): Record<string, BotCheckoutSessionEntry> {
  try {
    return JSON.parse(localStorage.getItem(BOT_CHECKOUT_INDEX_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function saveCheckoutIndex(sessionId: string, uuid: string, expiresAtMs: number) {
  const index = readCheckoutIndex();
  index[sessionId] = { uuid, expiresAt: expiresAtMs };
  localStorage.setItem(BOT_CHECKOUT_INDEX_KEY, JSON.stringify(index));
}

/**
 * Stamp siteConfig.rates and recompute mixed-currency totals the same way
 * normal B2C add-to-cart does (calcDpTotalUtil: mixed → USD).
 * Prefer live siteConfig.rates over BE exchange_rates — tbl_currency_rates can
 * be currency-per-ILS while siteConfig is ILS-per-unit.
 */
function withConvertedTotals(
  cart: ShoppingCartV2,
  rates?: ratesInterface,
): ShoppingCartV2 {
  const exchange_rates =
    (rates && Object.keys(rates).length
      ? rates
      : cart.exchange_rates && Object.keys(cart.exchange_rates).length
        ? cart.exchange_rates
        : undefined) ?? undefined;
  if (!exchange_rates || !Object.keys(exchange_rates).length) {
    return { ...cart, exchange_rates: cart.exchange_rates };
  }
  const { gross, net } = calculateCartTotalsUtil({
    products: cart.products as any,
    cartCurrencySign: cart.currency_sign,
    rates: exchange_rates,
  });
  return {
    ...cart,
    exchange_rates,
    currency_sign: gross.currency,
    total_price: gross.total,
    total_net_price: net.total,
  };
}

function findValidCachedCheckout(
  sessionId: string,
): { uuid: string; cart: ShoppingCartV2 } | null {
  const cartV2All = JSON.parse(localStorage.getItem('cartV2') ?? '{}');
  const cartAll = JSON.parse(localStorage.getItem('cart') ?? '{}');

  const tryUuid = (uuid: string): { uuid: string; cart: ShoppingCartV2 } | null => {
    const cart = cartV2All[uuid] as ShoppingCartV2 | undefined;
    if (!cart || !Array.isArray(cart.products) || !cart.products.length) {
      return null;
    }
    if (!cartAll[uuid]) return null;
    return { uuid, cart };
  };

  const indexEntry = readCheckoutIndex()[sessionId];
  if (indexEntry && Date.now() <= indexEntry.expiresAt) {
    const hit = tryUuid(indexEntry.uuid);
    if (hit) return hit;
  }

  // Legacy fallback for carts stashed before the session index existed.
  for (const [uuid, rawCart] of Object.entries(cartV2All)) {
    const cart = rawCart as ShoppingCartV2;
    if (cart?.bot_session_id !== sessionId) continue;
    const cartTime = (cartAll[uuid] as { time?: number } | undefined)?.time ?? 0;
    if (Date.now() - cartTime > BOT_CHECKOUT_TTL_MS) continue;
    const hit = tryUuid(uuid);
    if (hit) return hit;
  }

  return null;
}

/**
 * Throws away every checkout uuid minted for this bot session.
 *
 * The cache above exists so a plain back-navigation out of /checkout does not
 * cost a second BE round trip and a second uuid. It must NOT survive a cart
 * mutation: a cached hit never re-asks the BE, so an entry minted before the
 * user added (or removed) a product would send them to /checkout/<old uuid>
 * and book the previous itinerary.
 *
 * Clears both storage shapes `findValidCachedCheckout` can hit:
 *   - the `bot_checkout_sessions` index entry, and
 *   - every `cartV2[uuid]` (+ its `cart[uuid]` sibling) stamped with this
 *     session id, which is what the legacy fallback scan matches on.
 */
export function invalidateBotCheckoutCache(sessionId: string): void {
  try {
    const index = readCheckoutIndex();
    const indexedUuid = index[sessionId]?.uuid;
    if (indexedUuid) {
      delete index[sessionId];
      localStorage.setItem(BOT_CHECKOUT_INDEX_KEY, JSON.stringify(index));
    }

    const cartV2All = JSON.parse(localStorage.getItem('cartV2') ?? '{}');
    const cartAll = JSON.parse(localStorage.getItem('cart') ?? '{}');
    const stale = Object.keys(cartV2All).filter(
      (uuid) =>
        uuid === indexedUuid ||
        (cartV2All[uuid] as ShoppingCartV2)?.bot_session_id === sessionId,
    );
    if (!stale.length) return;

    stale.forEach((uuid) => {
      delete cartV2All[uuid];
      delete cartAll[uuid];
    });
    localStorage.setItem('cartV2', JSON.stringify(cartV2All));
    localStorage.setItem('cart', JSON.stringify(cartAll));
  } catch {
    // Unavailable/corrupt localStorage — there is nothing cached to reuse
    // either, so the next checkout click just re-asks the BE.
  }
}

/**
 * Single web checkout handler for the bot.
 *
 * The backend (BotCartBuilderService) now assembles the full ShoppingCartV2 for
 * every supported product type (hotel + flight bundle), so the FE no longer
 * re-runs per-product reducers. Flow:
 *   1. GET /bot-agent/sessions/:id/checkout-cart → full V2 cart.
 *   2. Mint a checkout uuid (same `t<ts>_<rand>` format as useHandleCheckout).
 *   3. Write BOTH localStorage keys the Checkout page reads:
 *        - cart[uuid]   = { shoppingCart, settings, time }  (the bounce-guard
 *          in Checkout.tsx navigates home if cart[uuid] is missing)
 *        - cartV2[uuid] = the V2 cart (the actual render source).
 *   4. dispatch(setWholeCart) so the live store matches the stashed cart.
 *   5. navigate('/checkout/<uuid>').
 *
 * `bot_session_id` is stamped on the cart for back-office order↔chat linkage
 * (the BE builder intentionally omits it — see the cart fixtures contract).
 *
 * This is the single web-checkout entry point, shared by:
 *   - the chat CTA button + CheckoutPanel (web, no token), and
 *   - the WhatsApp deep-link landing (`useBotSessionHydration`, which passes the
 *     signed `token` from the URL).
 *
 * Resolves `true` once the user has been navigated to /checkout, `false` when
 * the hand-off failed. It never rejects: the callers are click handlers with no
 * catch, so a rejection escaping here surfaces as an unhandled promise
 * rejection and the user sees nothing but a blinking spinner. Every failure
 * path instead ends in a `message.error` toast (the app-wide failure signal —
 * see ShoppingCartHeader / apiClient).
 */
export function useBotCheckoutHandler(): (
  sessionId: string,
  token?: string,
) => Promise<boolean> {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useSelector(
    (s: RootState) => (s.config.translations as any)?.bot,
  );
  const siteRates = useSelector(
    (s: RootState) => s.config?.config?.siteConfig?.rates as ratesInterface | undefined,
  );
  return useCallback(
    async (sessionId: string, token?: string): Promise<boolean> => {
      // Every B2C add-to-cart reducer runs `initialStateShoppingCart`, which
      // sets `state.agent_commission.percentage` to 0 for anyone who is not a
      // gross-view agent. The bot hand-off dispatches only `setWholeCart`,
      // which replaces `ShoppingCartV2` and does not own that field — so
      // `agent_commission` kept the slice default of **5**, `OrderDetailsForm`
      // posted it verbatim, and `order.service.ts` wrote it to
      // `tbl_orders.commission_data`. Every provider then re-priced the order on
      // the agent branch at a 5% commission the website never charges.
      //
      // Normalising here is the whole fix on this side: the same value
      // `initialStateShoppingCart` would have computed, applied on both
      // branches below because both reach /checkout without running one.
      const cachedCheckout = !token ? findValidCachedCheckout(sessionId) : null;

      if (cachedCheckout) {
        dispatch(
          agentCommissionPercent(
            botCheckoutAgentCommissionPercent(cachedCheckout.cart),
          ),
        );
        const cartForReuse = withConvertedTotals(
          {
            ...cachedCheckout.cart,
            bot_session_id: sessionId,
          } as ShoppingCartV2,
          siteRates,
        );
        dispatch(setWholeCart(cartForReuse));
        navigate(`/checkout/${cachedCheckout.uuid}`);
        return true;
      }

      let cart: any;
      try {
        cart = await getCheckoutCart(sessionId, token);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          '[useBotCheckoutHandler] checkout-cart request failed for session',
          sessionId,
          e,
        );
        message.error(
          t?.checkout?.failed ?? 'We could not open checkout. Please try again.',
        );
        return false;
      }

      if (!cart || !Array.isArray(cart.products) || !cart.products.length) {
        // eslint-disable-next-line no-console
        console.warn(
          '[useBotCheckoutHandler] empty/invalid checkout cart for session',
          sessionId,
        );
        message.error(
          t?.checkout?.emptyCart ??
            'Your cart is empty — add something before checking out.',
        );
        return false;
      }

      dispatch(agentCommissionPercent(botCheckoutAgentCommissionPercent(cart)));

      const cartForStash = withConvertedTotals(
        {
          ...(cart as ShoppingCartV2),
          // Linkage for order creation (CheckoutApis forwards this to the BE).
          bot_session_id: sessionId,
        } as ShoppingCartV2,
        siteRates,
      );

      const uuid = generateUUID();

      // cart[uuid] — Checkout.tsx bounces home if this is missing while a
      // cartV2 entry exists. Mirror the shape useHandleCheckout writes.
      const prevCart = JSON.parse(localStorage.getItem('cart') ?? '{}');
      localStorage.setItem(
        'cart',
        JSON.stringify({
          ...prevCart,
          [uuid]: {
            shoppingCart: cartForStash,
            settings: cart.settings ?? { show_guest_passport: true },
            time: Date.now(),
          },
        }),
      );

      // cartV2[uuid] — the render source for /checkout/:uuid.
      const prevCartV2 = JSON.parse(localStorage.getItem('cartV2') ?? '{}');
      localStorage.setItem(
        'cartV2',
        JSON.stringify({ ...prevCartV2, [uuid]: cartForStash }),
      );

      saveCheckoutIndex(sessionId, uuid, Date.now() + BOT_CHECKOUT_TTL_MS);

      dispatch(setWholeCart(cartForStash));
      navigate(`/checkout/${uuid}`);
      return true;
    },
    [navigate, dispatch, t, siteRates],
  );
}

/**
 * Pull `bot_session_id` out of a path like `/shopping-cart?bot_session_id=<uuid>`.
 * Returns undefined for anything that doesn't carry the param.
 */
export function extractBotSessionId(url: string): string | undefined {
  const m = url.match(/[?&]bot_session_id=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : undefined;
}
